import { FC, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';

import { styled } from '@mui/material/styles';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { financeTransactionsByCompanyStateLoadableJ, transactionsByIdStateJ } from '../state/FinanceStateJ';
import { transactionHistoryByCompanyState } from '../state/FinanceStateJotai';
import { useTransactionHistoryColumns } from '../../FinanceHistory/hooks/useTransactionHistoryColumns';
import { Loader } from '../../../components/Loader/Loader';
import { ErrorOccurredMessage } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { FinanceHistoryPage } from '../../FinanceHistory/components/FinanceHistoryContent';
import { companyMetricsLoadableJ } from '../../../services/state/CompanyMetricsStateJ';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { PANEL_WIDTH_IN_PX } from '../../FinanceHistory/components/CompanyTransactionsPanel/CompanyTransactionsPanel';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { SelectedCompanyParam } from '../finance-constants';

const ViewContainer = styled('div')`
  position: relative;
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: ${PANEL_WIDTH_IN_PX}px 1fr;
  max-height: calc(100vh - 12rem);
  overflow: auto;
`;

export const TransactionHistory: FC = () => {
  const [search] = useSearchParams();

  const companyParam = search.get('company');
  const companyId = companyParam ? parseInt(companyParam) : -1;
  const company = useAtomValue(companyStateJ(companyId));

  const transactionId = search.get('transaction');
  const transactionsById = useAtomValue(transactionsByIdStateJ(companyId));
  const selectedTransaction = transactionsById.get(transactionId ?? '');

  const historyLoadableAtom = useMemo(
    () => loadable(transactionHistoryByCompanyState(companyId)),
    [companyId]
  );
  const historyData = useAtomValue(historyLoadableAtom);
  const transactionsLoadable = useAtomValue(financeTransactionsByCompanyStateLoadableJ(companyId));
  const transactionHistory = historyData.state === 'hasData' ? historyData.data : undefined;

  const transactions = transactionsLoadable.state === 'hasData' ? transactionsLoadable.data : undefined;

  const companyMetricsData = useAtomValue(companyMetricsLoadableJ({ companyId }));
  const companyMetrics = companyMetricsData.state === 'hasData' ? companyMetricsData.data : undefined;

  const getTransactionHistoryColumns = useTransactionHistoryColumns();

  const tableColumns = useMemo(
    () => (!selectedTransaction ? [] : getTransactionHistoryColumns(selectedTransaction)),
    [selectedTransaction, getTransactionHistoryColumns]
  );

  const breadcrumbs = useMemo(() => {
    if (!company) {
      return [];
    }
    return [
      <Link
        key='transactions'
        component={RouterLink}
        color='secondary'
        variant='caption'
        underline='none'
        to={`/${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}/${ROUTES.FINANCE_DETAILS}/${ROUTES.FINANCE_TRANSACTIONS_3}?${SelectedCompanyParam}=${companyId}`}
      >
        Transactions
      </Link>,

      <Typography key='audits' color='text.primary' variant='caption'>
        {`${company.name} Transactions History`}
      </Typography>,
    ];
  }, [company, companyId]);

  if (
    historyData.state === 'loading' ||
    transactionsLoadable.state === 'loading' ||
    companyMetricsData.state === 'loading'
  ) {
    return <Loader />;
  }
  if (
    historyData.state === 'hasError' ||
    transactionsLoadable.state === 'hasError' ||
    companyMetricsData.state === 'hasError'
  ) {
    return <ErrorOccurredMessage />;
  }

  if (
    !transactionHistory ||
    !transactions ||
    !companyMetrics ||
    companyMetrics.metrics.length === 0 ||
    !company
  ) {
    return null;
  }

  return (
    <ViewContainer>
      <FinanceHistoryPage
        transactions={transactions}
        selectedTransaction={selectedTransaction ?? null}
        colDefs={tableColumns}
        companyMetrics={{ ...companyMetrics.metrics[0], company }}
        transactionHistory={transactionHistory}
        breadCrumbsComponent={
          <Breadcrumbs separator={<NavigateNextIcon fontSize='small' />} style={{ marginBottom: '0.5rem' }}>
            {breadcrumbs}
          </Breadcrumbs>
        }
        sidebarStyles={{ height: 'calc(100vh - 12rem)' }}
      />
    </ViewContainer>
  );
};
